import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import InsightsHeader from "@/components/insights/InsightsHeader";
import FeaturedInsightsSection from "@/components/insights/FeaturedInsightsSection";
import InsightsFilters from "@/components/insights/InsightsFilters";
import InsightsList from "@/components/insights/InsightsList";
import { useFilteredInsights } from "@/hooks/useFilteredInsights";
import { useCountry } from "@/contexts/country";
import UnifiedBreadcrumbs from "@/components/navigation/UnifiedBreadcrumbs";
import ResourceNavigationTabs from "@/components/navigation/ResourceNavigationTabs";
import { useUnifiedNavigation } from "@/hooks/navigation/useUnifiedNavigation";

const CountrySpecificInsights = () => {
  const { countryCode } = useParams<{ countryCode: string }>();
  const { availableCountries, setCountry, countryName } = useCountry();
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;
  const { handleCountryChange, generateBreadcrumbs } = useUnifiedNavigation();

  // Set country in context when countryCode changes
  useEffect(() => {
    if (countryCode) {
      setCountry(countryCode);
    }
  }, [countryCode, setCountry]);

  // Use the existing hook to fetch insights data for the specific country
  const { insights, isLoading, error } = useFilteredInsights(
    categoryFilter,
    countryCode,
    20
  );

  // Get country name for display
  const getCountryName = () => {
    if (!countryCode) return undefined;
    const country = availableCountries.find((c) => c.code === countryCode);
    return country ? country.name : countryCode;
  };

  const displayCountryName = getCountryName();
  const breadcrumbs = generateBreadcrumbs(
    "insights",
    countryCode,
    displayCountryName
  );

  // Handle category filter change
  const handleCategoryChange = (category: string | null) => {
    setCategoryFilter(category);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handle search query change
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page when search changes
  };

  // Calculate pagination
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;

  // Filter insights based on search query
  const filteredInsights = insights.filter(
    (insight) =>
      insight.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      insight.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get current page insights
  const currentInsights = filteredInsights.slice(startIndex, endIndex);

  // Calculate total pages
  const totalPages = Math.ceil(filteredInsights.length / pageSize);

  useEffect(() => {
    document.title = `Insights - ${displayCountryName || "Country"} | NationCard™`;
  }, [displayCountryName]);

  return (
    <MainLayout>
      <div
        className="container max-w-screen-xl mx-auto px-4 py-6"
        key={countryCode}
      >
        <ResourceNavigationTabs
          countryCode={countryCode}
          countryName={displayCountryName}
          showCountrySelector={true}
          availableCountries={availableCountries}
          onCountryChange={handleCountryChange}
        />

        <InsightsHeader />

        <div className="mb-4">
          <h1 className="text-3xl font-bold mb-8 text-nation-blue dark:text-blue-300">
            {displayCountryName
              ? `${displayCountryName} Insights`
              : "Country Insights"}
          </h1>
          <p className="text-muted-foreground">
            Discover key insights from {displayCountryName || "this country"}{" "}
            across various categories and topics
          </p>
        </div>

        {/* Featured Insights Section */}
        <section className="mb-12">
          <FeaturedInsightsSection />
        </section>

        {/* Browse All Insights Section */}
        <section>
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-6 text-nation-blue dark:text-blue-300">
              Browse All Insights
            </h2>
            <p className="text-gray-500 dark:text-gray-400">
              Explore all insights and filter by category or search by keyword
            </p>
          </div>

          {/* Filters */}
          <InsightsFilters
            onCategoryChange={handleCategoryChange}
            onSearchChange={handleSearchChange}
            selectedCategory={categoryFilter}
            searchQuery={searchQuery}
            key={`filters-${countryCode}`}
          />

          {/* Insights Grid */}
          <InsightsList
            insights={currentInsights}
            isLoading={isLoading}
            error={error}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            key={`insights-${countryCode}`}
          />
        </section>
      </div>
    </MainLayout>
  );
};

export default CountrySpecificInsights;
